import { quizResult, sandboxState } from './sandbox'
import { simulateWheelRound } from './wheel'

type Route = {
    method: string
    pattern: RegExp
    answer: (params: any, lang: string) => any
}

const ok = (data: any) => ({ statusCode: 200, data })

/** Les questions saisies dans le CMS. */
const soloQuiz = (): { questions: any[] } => {
    const config = sandboxState.game?.soloQuizz ?? {}
    return { questions: (config.questions ?? []).filter((question: any) => question?.question) }
}

/** Une question servie : comme le back la sert, sans sa bonne réponse. */
const serveSoloQuestion = (questions: any[], number: number, lang: string) => {
    const question = questions[number - 1]
    const text = (field: string) => (lang === 'en' && question[`${field}_en`]) || question[field] || ''
    return {
        done: false,
        number,
        total: questions.length,
        question: text('question'),
        answers: [1, 2, 3, 4].map((n) => ({ number: n, answer: text(`answer${n}`) })).filter((a) => a.answer)
    }
}

/**
 * Les routes que le bac à sable sait simuler. Les autres sont REFUSÉES : on ne
 * compte pas sur des `isTest` dans le back, une requête non prévue ne part pas.
 */
const ROUTES: Route[] = [
    // L'inscription, commune à tous les jeux.
    {
        method: 'GET',
        pattern: /^events-games\/[^/]+\/participations\/me$/,
        answer: () =>
            sandboxState.registered
                ? ok({ _id: 'preview-participation', photos: Array.from({ length: sandboxState.photos }, (_, i) => ({ _id: `preview-photo-${i}` })) })
                : { statusCode: 404, error: 'Not Found' }
    },
    {
        method: 'POST',
        pattern: /^events-games\/[^/]+\/participations$/,
        answer: () => {
            sandboxState.registered = true
            return ok({ participation: { _id: 'preview-participation' } })
        }
    },
    {
        method: 'GET',
        pattern: /^events-games\/[^/]+\/participations\/prefill$/,
        answer: () => ok({ answers: [] })
    },
    // Jouer : la réponse d'un jeu formulaire, ou la fin d'une séquence Tifo.
    {
        method: 'PATCH',
        pattern: /^events-games\/[^/]+\/participations\/me$/,
        answer: () => (sandboxState.registered ? ok({ _id: 'preview-participation' }) : { statusCode: 403, error: 'Forbidden' })
    },
    // Le photobooth : trois photos au plus. Elles ne quittent pas le navigateur.
    {
        method: 'POST',
        pattern: /^events-games\/[^/]+\/participations\/me\/photos$/,
        answer: () => {
            if (sandboxState.photos >= 3) return { statusCode: 409, error: 'Conflict' }
            sandboxState.photos += 1
            return ok({ _id: `preview-photo-${sandboxState.photos}` })
        }
    },
    // Le quiz : le résultat que le meneur du CMS a calculé. `error: ''` : le
    // service teste `res.error.length`.
    {
        method: 'GET',
        pattern: /^events-games\/[^/]+\/lucky-quizz\/ranking$/,
        answer: () => ({ ...ok(quizResult.mine ?? { rightAnswersCount: 0, rank: 0, wonGain: null }), error: '' })
    },
    {
        method: 'GET',
        pattern: /^events-games\/[^/]+\/full-ranking$/,
        answer: () => ok(quizResult.ranking)
    },
    // Le quiz individuel : le back sert les questions une à une. Ici, c'est le
    // bac à sable — sur les questions SAISIES, et sans jamais rendre la bonne
    // réponse d'avance.
    {
        method: 'POST',
        pattern: /^events-games\/[^/]+\/solo-quizz\/next$/,
        answer: (_params, lang) => {
            const { questions } = soloQuiz()
            if (questions.length === 0) return { statusCode: 400, error: 'No question' }
            const solo = sandboxState.solo
            // Une question servie et pas encore répondue est resservie telle quelle.
            const pending = solo.served > solo.answered
            if (!pending) {
                if (solo.answered >= questions.length) {
                    return ok({ done: true, total: questions.length, rightAnswersCount: solo.right, score: solo.right * 10 })
                }
                solo.served = solo.answered + 1
                solo.servedAt = Date.now()
            }
            return ok(serveSoloQuestion(questions, solo.served, lang))
        }
    },
    {
        method: 'POST',
        pattern: /^events-games\/[^/]+\/solo-quizz\/answer$/,
        answer: (params) => {
            const { questions } = soloQuiz()
            const solo = sandboxState.solo
            const number = Number(params?.questionNumber)
            if (number !== solo.served || solo.answered >= number) {
                return { statusCode: 400, error: 'Question not served' }
            }
            const rightAnswer = Number(questions[number - 1]?.rightAnswer)
            const right = params?.answer !== undefined && Number(params.answer) === rightAnswer
            solo.answered = number
            if (right) solo.right += 1
            return ok({ right, rightAnswer, done: number >= questions.length })
        }
    },
    // La roue.
    {
        method: 'GET',
        pattern: /^events-games\/[^/]+\/lucky-wheel\/rounds-count$/,
        answer: () => ok({ roundsCount: sandboxState.roundsLeft })
    },
    {
        method: 'POST',
        pattern: /^events-games\/[^/]+\/lucky-wheel\/ranking$/,
        answer: (_params, lang) =>
            sandboxState.roundsLeft > 0 ? ok(simulateWheelRound(lang)) : { statusCode: 400, error: 'No more rounds' }
    }
]

/** La réponse simulée d'une requête, à la forme de `createHttpRequest`. */
export const sandboxRequest = async (url: string, method: string, params: any, lang: string): Promise<any> => {
    const path = url.replace(/^\/+/, '').split('?')[0]
    const route = ROUTES.find((r) => r.method === method && r.pattern.test(path))
    if (!route) {
        console.warn(`[aperçu] requête non simulée, bloquée : ${method} ${path}`)
        return { statusCode: 501, error: 'Not simulated in preview' }
    }
    // Un léger délai : la roue tourne pendant qu'elle « attend le serveur ».
    await new Promise((resolve) => setTimeout(resolve, 400))
    return route.answer(params, lang)
}
